import React, { useEffect, useState } from "react";
import { Loader2, Search, Boxes, AlertTriangle } from "lucide-react";
import api, { money } from "../lib/api";
import { smartSearch } from "../lib/search";
import { useSortable } from "../lib/sortable";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

export default function StockLookup() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/stock").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const { Th, sortRows } = useSortable();
  const filtered = smartSearch(products, q, ["code", "description", "brand", "category"]);
  const lowCount = products.filter((p) => p.low).length;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-heading font-bold">Consulta de existencias</h1>
        <p className="text-sm text-muted-foreground">Busca productos y revisa disponibilidad. Solo lectura.</p>
      </div>
      {lowCount > 0 && (
        <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-600 rounded-sm px-4 py-2.5 text-sm" data-testid="stock-low-alert">
          <AlertTriangle className="h-4 w-4" /> <strong>{lowCount}</strong> producto(s) con existencia igual o por debajo del mínimo.
        </div>
      )}
      <div className="relative w-full sm:w-96">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="stock-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por código, descripción, marca..." className="pl-9 rounded-sm" />
      </div>
      <Card className="rounded-sm border shadow-none overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> :
          filtered.length === 0 ? <div className="flex flex-col items-center py-16 text-muted-foreground"><Boxes className="h-8 w-8 mb-2" /><p className="text-sm">Sin productos.</p></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="stock-table">
                <thead><tr className="bg-muted text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <Th field="code">Código</Th><Th field="description">Descripción</Th>
                  <Th field="brand">Marca</Th><Th field="stock" align="right" className="text-right">Físico</Th>
                  <Th field="reserved" align="right" className="text-right">Reservado</Th><Th field="available" align="right" className="text-right">Disponible</Th>
                  <Th field="price" align="right" className="text-right">Precio</Th></tr></thead>
                <tbody>
                  {sortRows(filtered).map((p) => (
                    <tr key={p.id} className="border-t border-border hover:bg-muted/50" data-testid={`stock-row-${p.code}`}>
                      <td className="px-4 py-2.5 font-mono text-xs">{p.code}</td>
                      <td className="px-4 py-2.5 font-medium">{p.description}{p.low && <Badge className="ml-2 rounded-sm text-[10px] bg-amber-100 text-amber-700">Stock bajo</Badge>}</td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{p.brand || "—"}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{p.stock}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-muted-foreground">{p.reserved}</td>
                      <td className={`px-4 py-2.5 text-right font-mono font-bold ${p.available <= 0 ? "text-destructive" : ""}`}>{p.available}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{money(p.currency, p.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
}
